import React, { useEffect, useState } from 'react';

import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader/PageHeader';
import SummaryCards from '../../../components/Reports/SummaryCards';
import { getInventoryDashboardData } from '../../../services/ReportsInventoryService';
import LowStockByBranchGrid from '../../../components/Reports/Inventory/LowStockByBranchGrid';
import OutOfStockByBranchGrid from '../../../components/Reports/Inventory/OutOfStockByBranchGrid';
import StockValueByBranchChart from '../../../components/Reports/Inventory/StockValueByBranchChart';
import ProjectedStockoutsByBranchGrid from '../../../components/Reports/Inventory/ProjectedStockoutsByBranchGrid';
import MonthlyProductionChart from '../../../components/Reports/Inventory/MonthlyProductionChart';
import MostSoldProductsTable from '../../../components/Reports/Inventory/MostSoldProductsTable';
import UnsoldProductsTable from '../../../components/Reports/Inventory/UnsoldProductsTable';
import CycleCoverageByBranchGrid from '../../../components/Reports/Inventory/CycleCoverageByBranchGrid';
import RecurringDifferencesTable from '../../../components/Reports/Inventory/RecurringDifferencesTable';
import { getStockCountCoverageReport } from '../../../services/StockCountService';

import {
  Warning as WarningIcon,
  MonetizationOn as MonetizationOnIcon,
  RemoveShoppingCart as OutOfStockIcon,
  Factory as FactoryIcon
} from '@mui/icons-material';
import { formatMoney } from '../../../utils/currency';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
const InventoryReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  
  const { t } = useTranslation();
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockCountReport, setStockCountReport] = useState(null);
  const [stockCountLoading, setStockCountLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const result = await getInventoryDashboardData({ month, year });
      setData(result);
      setLoading(false);
    };
    fetch();
  }, [month, year, reportCurrency]);

  useEffect(() => {
    const fetchStockCountReport = async () => {
      try {
        setStockCountLoading(true);
        const result = await getStockCountCoverageReport();
        setStockCountReport(result);
      } catch (error) {
        // Plugin "conteo de stock" apagado o sin permiso -- la sección simplemente no se muestra.
        setStockCountReport(null);
      } finally {
        setStockCountLoading(false);
      }
    };
    fetchStockCountReport();
  }, []);

  const summaryItems = data?.summary
    ? [
        {
          title: 'Valor del stock total',
          icon: <MonetizationOnIcon color="primary" fontSize="large" />,
          value: `${formatMoney(data.summary.totalStockValue)}`,
        },
        {
          title: 'Insumos críticos de stock',
          icon: <WarningIcon color="error" fontSize="large" />,
          value: `${data.summary.criticalSupplies} insumos`,
        },
        {
          title: 'Productos sin stock',
          icon: <OutOfStockIcon color="disabled" fontSize="large" />,
          value: `${data.summary.outOfStockProducts} productos`,
        },
        {
          title: 'Producidos este mes',
          icon: <FactoryIcon color="success" fontSize="large" />,
          value: `${data.summary.productsProducedThisMonth} unidades`,
        },
      ]
    : [];

  return (
    <Box p={3}>
      {/* Header y selector de mes/año */}
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title={t('Stock y Producción')} subtitle={t('Monitoreo del stock y el desempeño de producción mensual')} />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
      </Grid>

      {/* Summary */}
      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      <Grid container spacing={2} mt={0} mb={4}>
        <Grid item xs={4}>
          <StockValueByBranchChart
            branches={data?.branches || []}
            data={data?.stockValueByBranch || {}}
            loading={loading}
          />
        </Grid>
        <Grid item xs={8}>
          <MonthlyProductionChart data={data?.producedByMonth || []} loading={loading} />
        </Grid>
      </Grid>
      
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={6}>
          <MostSoldProductsTable data={data?.mostSoldProducts || []} loading={loading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <UnsoldProductsTable data={data?.unsoldProducts || []} loading={loading} />
        </Grid>
      </Grid>

      <OutOfStockByBranchGrid
        branches={data?.branches || []}
        outOfStockByBranch={data?.outOfStockByBranch || {}}
        loading={loading}
      />

      <LowStockByBranchGrid
        branches={data?.branches || []}
        data={data?.lowStockByBranch || {}}
        loading={loading}
      />

      <ProjectedStockoutsByBranchGrid
        branches={data?.branches || []}
        data={data?.projectedStockouts || {}}
        loading={loading}
      />

      <CycleCoverageByBranchGrid data={stockCountReport?.branches || []} loading={stockCountLoading} />
      <RecurringDifferencesTable data={stockCountReport?.recurringDifferences || []} loading={stockCountLoading} />
    </Box>
  );
};

export default InventoryReports;
